import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Car, User, Calendar, AlertCircle, Loader2, CheckCircle, Clock } from 'lucide-react';
import FirmaCanvas from '../components/FirmaCanvas';

const API_URL = '/api';

/**
 * FirmaRemota - Página pública para firma remota via enlace
 * Optimizada para móvil, no requiere autenticación
 */
function FirmaRemota() {
  const { token } = useParams();
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [firmaImagen, setFirmaImagen] = useState(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validarToken();
  }, [token]);

  const validarToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/firmas/validar-token/${token}`);
      setDatos(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Enlace inválido o expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleFirmar = async () => {
    if (!firmaImagen) {
      setError('Por favor, dibuje su firma');
      return;
    }

    if (!aceptaTerminos) {
      setError('Debe aceptar los términos y condiciones');
      return;
    }

    setEnviando(true);
    setError('');

    try {
      await axios.post(`${API_URL}/firmas/registrar`, {
        token,
        firmaImagen,
        aceptaTerminos: true
      });

      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrar la firma');
    } finally {
      setEnviando(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error && !datos) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Enlace no válido</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-500">
            Si necesita firmar un documento, solicite un nuevo enlace a su empresa.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Firma Registrada</h1>
          <p className="text-gray-600 mb-4">
            Su firma ha sido registrada correctamente.
          </p>
          {datos.conductor_email && (
            <p className="text-sm text-gray-500">
              Recibirá una copia del documento en su email: <strong>{datos.conductor_email}</strong>
            </p>
          )}
        </div>
      </div>
    );
  }

  const tipoTexto = datos.tipo_firma === 'ENTREGA' ? 'Entrega' : 'Devolución';
  const colorHeader = datos.tipo_firma === 'ENTREGA'
    ? 'from-green-600 to-green-700'
    : 'from-red-600 to-red-700';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className={`bg-gradient-to-r ${colorHeader} text-white py-6 px-4`}>
        <div className="max-w-lg mx-auto">
          <h1 className="text-2xl font-bold text-center">
            Firma de {tipoTexto}
          </h1>
          <p className="text-center text-white/80 mt-1">
            Documento de custodia de vehículo
          </p>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 pb-8">
        {/* Información del documento */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-gray-900 mb-3">Datos del documento</h2>

          <div className="space-y-3">
            {/* Empresa */}
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Car className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-500">Empresa</p>
                <p className="font-medium text-gray-900">{datos.empresa_nombre}</p>
              </div>
            </div>

            {/* Vehículo */}
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-gray-700 text-xs">{datos.matricula?.slice(0, 3)}</span>
              </div>
              <div>
                <p className="text-gray-500">Matrícula</p>
                <p className="font-bold text-xl text-gray-900">{datos.matricula}</p>
              </div>
            </div>

            {/* Conductor */}
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-gray-500">Conductor</p>
                <p className="font-medium text-gray-900">{datos.conductor_nombre}</p>
                <p className="text-gray-500">DNI: {datos.conductor_dni}</p>
              </div>
            </div>

            {/* Fecha */}
            <div className="flex items-center gap-3 text-sm">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-gray-500">Fecha de {datos.tipo_firma === 'ENTREGA' ? 'entrega' : 'devolución'}</p>
                <p className="font-medium text-gray-900">
                  {new Date().toLocaleDateString('es-ES', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cláusulas */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-gray-900 mb-2">Términos y Condiciones</h2>
          <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto text-sm text-gray-700 whitespace-pre-line">
            {datos.clausulas || 'El firmante declara haber recibido/devuelto el vehículo indicado y acepta las condiciones de uso establecidas.'}
          </div>
        </div>

        {/* Canvas de firma */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-gray-900 mb-2">Su Firma</h2>
          <p className="text-sm text-gray-500 mb-3">
            Dibuje su firma en el recuadro con el dedo
          </p>
          <FirmaCanvas
            onFirmaChange={setFirmaImagen}
            width={350}
            height={180}
          />
        </div>

        {/* Checkbox de aceptación */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={aceptaTerminos}
              onChange={(e) => setAceptaTerminos(e.target.checked)}
              className="w-5 h-5 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              He leído y acepto los términos y condiciones. Confirmo que los datos son correctos y que la firma es mía.
            </span>
          </label>
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg mb-4">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Botón firmar */}
        <button
          onClick={handleFirmar}
          disabled={enviando || !firmaImagen || !aceptaTerminos}
          className={`w-full py-4 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-colors ${
            datos.tipo_firma === 'ENTREGA'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {enviando ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Registrando firma...
            </>
          ) : (
            <>Firmar {tipoTexto}</>
          )}
        </button>

        {/* Token expiration warning */}
        {datos.token_expiracion && (
          <div className="mt-4 flex items-center justify-center gap-2 text-gray-500 text-sm">
            <Clock className="w-4 h-4" />
            <span>
              Enlace válido hasta: {new Date(datos.token_expiracion).toLocaleString('es-ES')}
            </span>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>Sistema de Gestión de Multas</p>
          <p>{datos.empresa_nombre}</p>
        </div>
      </main>
    </div>
  );
}

export default FirmaRemota;
